/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import { HeroModule, MODULE_LAYOUTS, isInvertedColor } from '@coursera-web/cds-labs';

import config from 'js/app/config';
import { useLocation, useNavigate } from 'js/lib/useRouter';

import { Button, Hidden, breakpoints } from '@coursera/cds-core';
import type { ButtonName } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import cplusBannerMobileImage from 'images/front-page/cplus-upsell-banner-image-mobile.png';
import cplusBannerImageNoCrop from 'images/front-page/cplus-upsell-banner-image-nocrop.png';
import cplusInvertImage from 'images/unified-common/coursera-plus-logo-invert.svg';
import cplusBannerImage from 'images/unified-common/cplus-upsell-lohp-hero-desktop.png';

import _t from 'i18n!nls/front-page';

type HeroModuleProps = React.ComponentProps<typeof HeroModule>;

const styles = {
  container: css`
    ${breakpoints.down('xs')} {
      text-align: center;
    }
  `,

  heading: css`
    ${breakpoints.down('xs')} {
      text-align: center;
    }
  `,
  description: css`
    margin-bottom: var(--cds-spacing-200);
    white-space: pre-line;

    ${breakpoints.up('md')} {
      width: 80%;
    }
  `,
  button: css`
    text-decoration: none;

    ${breakpoints.down('xs')} {
      width: 100%;
    }
  `,
  secondaryButton: css`
    && {
      background-color: transparent;
    }
  `,
  buttonWithBorder: css`
    &&,
    &:hover {
      border: 1px solid var(--cds-color-interactive-stroke-primary-focus-invert);
    }
  `,
};

const CourseraUpsellHeroModuleRenderer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const track = useTracker();
  const trackClick = (buttonName: ButtonName, linkName: string, linkTarget: string) => () => {
    track('click_button', {
      button: { name: buttonName, linkName, linkURL: linkTarget, linkType: 'internal' },
      pageSection: {
        sectionName: 'hero_banner',
      },
    });
  };

  // hard-coded data to be moved to contentful, post-experiment.
  const heroRenderingContent = {
    headline1: _t('Build your skills.'),
    headline2: _t('Boost your career.'),
    description: (
      <FormattedMessage
        message={_t(
          'Access {courseCount}+ world-class courses, get guidance with Coursera Coach and earn recognized credentials - all with one subscription.'
        )}
        courseCount={<FormattedNumber value={10000} />}
      />
    ),
    shortDescription: '',
    backgroundColor: 'blue',
    ctaText: _t('Learn more about Coursera Plus'),
    ctaTarget: `${config.url.base}courseraplus`,
    secondaryCtaText: _t('Join for Free'),
    secondaryCtaTarget: '?authMode=signup',
    image: {
      url: cplusBannerImage,
      description: '',
    },
    imageTablet: {
      url: cplusBannerImageNoCrop,
      description: '',
    },
    imageMobile: {
      url: cplusBannerMobileImage,
      description: '',
    },
    eyebrowImage: {
      url: cplusInvertImage,
      description: _t('Coursera Plus Image.'),
    },
  };

  const {
    headline1,
    headline2,
    description,
    shortDescription,
    backgroundColor,
    ctaText,
    ctaTarget,
    secondaryCtaTarget,
    secondaryCtaText,
    image,
    imageTablet,
    imageMobile,
    eyebrowImage,
  } = heroRenderingContent;

  const handleSecondaryCtaClick = () => {
    const trackFn = trackClick('hero_secondary_cta', secondaryCtaText, secondaryCtaTarget);
    trackFn();

    const params = new URLSearchParams(location.search);
    params.set('authMode', 'signup');
    const signupUrl = `${location.pathname}?${params.toString()}`;
    navigate(signupUrl, { replace: true });
  };

  const titleToShow = (titleCopy1: string, titleCopy2: string) => (
    <React.Fragment>
      <div css={styles.heading}>{titleCopy1}</div>
      <div css={styles.heading}>{titleCopy2}</div>
    </React.Fragment>
  );
  const descriptionToShow = (descriptionCopy: string | JSX.Element) => (
    <p css={styles.description}>{descriptionCopy}</p>
  );

  const isDarkBackground = isInvertedColor(backgroundColor);

  const getHeroModuleProps = ({ isMobile, isTablet }: { isMobile?: boolean; isTablet?: boolean }): HeroModuleProps => {
    let imageUrl;

    if (isTablet) {
      imageUrl = imageTablet?.url;
    } else if (isMobile) {
      imageUrl = imageMobile?.url;
    } else {
      imageUrl = image?.url;
    }

    return {
      headline: titleToShow(headline1, headline2),
      description: descriptionToShow(isMobile && shortDescription ? shortDescription : description),
      image: {
        url: imageUrl,
        width: isMobile || isTablet ? 600 : 720,
      },
      eyebrow: {
        image: {
          url: eyebrowImage?.url,
          height: isMobile ? 12 : 16,
          description: eyebrowImage?.description,
        },
      },
      backgroundColor,
      layout: MODULE_LAYOUTS.text6image6,
      showImagesAboveContentOnMobile: false,
      preloadImage: true,
      customPrimaryCta:
        ctaText && ctaTarget ? (
          <Button
            component="a"
            variant={isDarkBackground ? 'secondary' : 'primary'}
            href={ctaTarget}
            css={styles.button}
            onClick={trackClick('hero_primary_cta', ctaText, ctaTarget)}
          >
            {ctaText}
          </Button>
        ) : undefined,
      customSecondaryCta: secondaryCtaText ? (
        <Button
          variant={isDarkBackground ? 'primary' : 'secondary'}
          css={[styles.button, styles.secondaryButton, isDarkBackground ? styles.buttonWithBorder : undefined]}
          onClick={handleSecondaryCtaClick}
        >
          {secondaryCtaText}
        </Button>
      ) : undefined,
    };
  };

  return (
    <div css={styles.container}>
      <Hidden smUp>
        <HeroModule {...getHeroModuleProps({ isMobile: true, isTablet: false })} />
      </Hidden>
      <Hidden mdUp xsDown>
        <HeroModule {...getHeroModuleProps({ isMobile: false, isTablet: true })} />
      </Hidden>
      <Hidden smDown>
        <HeroModule {...getHeroModuleProps({ isMobile: false, isTablet: false })} />
      </Hidden>
    </div>
  );
};

export default CourseraUpsellHeroModuleRenderer;
